// Per-client Assessments and Care Plan & ADLs views.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { resolveAssessmentPath } from '../../lib/documents/packets';
import { isDmeOnly } from '../../lib/serviceModel';
import CarePlanEditor from '../clinical/CarePlanEditor';
import ClientTaskList from '../clinical/ClientTaskList';
import { getClinicalSettings, assessmentStatus, diffAssessments } from '../../lib/clinicalSettings';

const card = { background: '#fff', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
const row = { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 };
const lbl = { color: 'var(--text3)' };
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US') : '—';

function useAssessments(client) {
  const [state, setState] = useState({ loading: true, rows: [] });
  useEffect(() => {
    let cancel = false;
    (async () => {
      const { data } = await supabase.from('nurse_assessments').select('*')
        .eq('client_uuid', client.id).order('assessment_date', { ascending: false });
      if (!cancel) setState({ loading: false, rows: data || [] });
    })();
    return () => { cancel = true; };
  }, [client]);
  return state;
}

export function ClientAssessments({ client }) {
  const navigate = useNavigate();
  const { loading, rows } = useAssessments(client);
  const [settings, setSettings] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  useEffect(() => { getClinicalSettings().then(setSettings); }, []);
  const latest = rows[0], previous = rows[1];
  const st = settings ? assessmentStatus(latest?.assessment_date || client.nurse_assessment_date, settings) : null;
  const changes = showDiff ? diffAssessments(latest, previous) : [];
  const tone = { danger: 'var(--danger)', warn: 'var(--warn)', success: 'var(--success)' };
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Nurse Assessments</h3>
        {!isDmeOnly(client) && (
          <button className="btn btn-sm" onClick={async () => navigate(await resolveAssessmentPath(client))}
            style={{ background: 'var(--accent)', color: 'var(--white)', border: 'none', borderRadius: 6, padding: '7px 12px', cursor: 'pointer' }}>
            {client.nurse_assessment_complete ? 'Review / Update' : 'Complete Assessment'}
          </button>
        )}
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={row}><span style={lbl}>Status</span>
          <span style={{ color: st ? tone[st.tone] : 'var(--text3)', fontWeight: 600 }}>{st ? st.label : '…'}</span></div>
        <div style={row}><span style={lbl}>Last assessment</span><span>{fmt(latest?.assessment_date || client.nurse_assessment_date)}</span></div>
        <div style={row}><span style={lbl}>Review due (every {settings?.assessment_review_days ?? '…'} days)</span><span>{st?.reviewDue ? st.reviewDue.toLocaleDateString('en-US') : '—'}</span></div>
        <div style={row}><span style={lbl}>Reassessment due (every {settings?.reassessment_days ?? '…'} days)</span><span>{st?.reassessDue ? st.reassessDue.toLocaleDateString('en-US') : '—'}</span></div>
        {loading ? <p style={{ color: 'var(--text3)' }}>Loading…</p>
          : rows.length === 0 ? <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 10 }}>No saved assessment records yet.</p>
          : rows.map((a, i) => (
            <div key={a.id} style={row}>
              <span>{fmt(a.assessment_date)} <span style={lbl}>v{a.version_number || rows.length - i}</span></span>
              <span style={lbl}>{a.status || 'submitted'}{a.completed_by_name ? ` · ${a.completed_by_name}` : ''}</span>
            </div>
          ))}
        {previous && (
          <div style={{ marginTop: 10 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowDiff(v => !v)}>{showDiff ? 'Hide changes' : 'What changed since the previous version'}</button>
            {showDiff && (changes.length === 0 ? <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>No differences between the two latest versions.</p> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 12 }}>
                <thead><tr><th style={{ textAlign: 'left', padding: 4, color: 'var(--text3)', fontWeight: 500 }}>Field</th><th style={{ textAlign: 'left', padding: 4, color: 'var(--text3)', fontWeight: 500 }}>Before</th><th style={{ textAlign: 'left', padding: 4, color: 'var(--text3)', fontWeight: 500 }}>After</th></tr></thead>
                <tbody>{changes.map(c => (
                  <tr key={c.field}><td style={{ padding: 4, verticalAlign: 'top', textTransform: 'capitalize' }}>{c.field}</td>
                    <td style={{ padding: 4, verticalAlign: 'top', color: 'var(--text3)' }}>{String(typeof c.before === 'object' ? JSON.stringify(c.before) : c.before ?? '—').slice(0, 140)}</td>
                    <td style={{ padding: 4, verticalAlign: 'top' }}>{String(typeof c.after === 'object' ? JSON.stringify(c.after) : c.after ?? '—').slice(0, 140)}</td></tr>
                ))}</tbody>
              </table>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ClientCarePlan({ client }) {
  // The plan decides which care is required and the nurse signs it; the task
  // list below decides when and how it is delivered and the office may adjust
  // it. Bumping `saved` re-reads the list after an activation changes it.
  const [saved, setSaved] = useState(0);
  return (
    <>
      <div style={card}>
        <h3 style={{ marginTop: 0 }}>Care Plan &amp; ADLs</h3>
        <CarePlanEditor client={client} onChanged={() => setSaved(n => n + 1)} />
      </div>
      <div style={card}>
        <ClientTaskList key={saved} client={client} />
      </div>
    </>
  );
}
